/** Level 1 topic areas. `firestore` is the exact string stored on questions. */

import { SYLLABUS_YEAR, TOPIC_WEIGHTS_2026, formatWeightRange } from './syllabus';
import { isSessionSitting, mockFormFromTopic, mockSittingLabel } from '../constants/mockSitting';

const BASE = [
  {
    slug: 'ethical-and-professional-standards',
    firestore: 'Ethical and Professional Standards',
    label: 'Ethical and Professional Standards',
    short: 'Ethics',
    weight: 0.155,
    sessionCount: 14,
    blurb: 'Code of Ethics, Standards I–VII, GIPS, and ethics application.',
  },
  {
    slug: 'quantitative-methods',
    firestore: 'Quantitative Methods',
    label: 'Quantitative Methods',
    short: 'Quant',
    weight: 0.075,
    sessionCount: 7,
    blurb: 'Rates of return, TVM, stats, hypothesis tests, regression, and big data.',
  },
  {
    slug: 'economics',
    firestore: 'Economics',
    label: 'Economics',
    short: 'Economics',
    weight: 0.075,
    sessionCount: 7,
    blurb: 'Market structure, cycles, policy, geopolitics, trade, and FX.',
  },
  {
    slug: 'financial-reporting-and-analysis',
    firestore: 'Financial Reporting and Analysis',
    label: 'Financial Statement Analysis',
    short: 'FSA',
    weight: 0.125,
    sessionCount: 11,
    blurb: 'Statements, inventories, taxes, reporting quality, and ratios. IFRS unless an item says US GAAP.',
  },
  {
    slug: 'corporate-issuers',
    firestore: 'Corporate Issuers',
    label: 'Corporate Issuers',
    short: 'Corporate',
    weight: 0.075,
    sessionCount: 7,
    blurb: 'Governance, business models, capital allocation, WACC, and working capital.',
  },
  {
    slug: 'equity-investments',
    firestore: 'Equity Investments',
    label: 'Equity Investments',
    short: 'Equity',
    weight: 0.125,
    sessionCount: 11,
    blurb: 'Market structure, indexes, efficiency, industry analysis, and valuation.',
  },
  {
    slug: 'fixed-income',
    firestore: 'Fixed Income',
    label: 'Fixed Income',
    short: 'Fixed income',
    weight: 0.125,
    sessionCount: 11,
    blurb: 'Bond features, pricing, yields, duration, credit, ABS, and MBS.',
  },
  {
    slug: 'derivatives',
    firestore: 'Derivatives',
    label: 'Derivatives',
    short: 'Derivatives',
    weight: 0.065,
    sessionCount: 6,
    blurb: 'Forwards, futures, swaps, options, put–call parity, and binomial pricing.',
  },
  {
    slug: 'alternative-investments',
    firestore: 'Alternative Investments',
    label: 'Alternative Investments',
    short: 'Alts',
    weight: 0.08,
    sessionCount: 7,
    blurb: 'Private capital, real estate, infrastructure, natural resources, hedge funds, and digital assets.',
  },
  {
    slug: 'portfolio-management-and-wealth-planning',
    firestore: 'Portfolio Management and Wealth Planning',
    label: 'Portfolio Management',
    short: 'Portfolio',
    weight: 0.1,
    sessionCount: 9,
    blurb: 'Risk and return, CAPM, IPS, behavioral biases, and risk management.',
  },
];

export const TOPICS = BASE.map((topic) => {
  const official = TOPIC_WEIGHTS_2026[topic.firestore];
  return {
    ...topic,
    examYear: SYLLABUS_YEAR,
    session: official.session,
    weightMin: official.min,
    weightMax: official.max,
    weightLabel: formatWeightRange(topic.firestore),
  };
});

export const TOPIC_BY_SLUG = Object.fromEntries(TOPICS.map((t) => [t.slug, t]));

export const SESSION_QUESTION_TARGET = 90;
export const SESSION_SECONDS = 135 * 60;

/**
 * Label for a stored topic: curriculum topic name, "Mock N" for a shipped mock form of `level`
 * (L1 1..MOCK_FORM_COUNT, L2/L3 their own counts — CFA-63), "Timed session" for legacy hub slugs.
 */
export function topicLabel(firestoreName, level = 'l1') {
  const match = TOPICS.find((t) => t.firestore === firestoreName);
  if (match) return match.label;
  const mock = mockSittingLabel(firestoreName, level);
  if (mock) return mock;
  const slug = String(firestoreName || '').toLowerCase();
  if (slug === 'session' || slug === 'all' || slug === 'mock') return 'Timed session';
  return firestoreName;
}

/** Human label for history / email / dashboard: "L2 · Mock 1", "Ethics", etc. */
export function attemptLabel({ topic, level = 'l1', mockForm = null } = {}) {
  const lvl = String(level || 'l1').toLowerCase();
  const prefix = lvl === 'l3' ? 'L3' : lvl === 'l2' ? 'L2' : lvl === 'l1' ? 'L1' : '';
  const formId = mockForm != null && Number(mockForm) >= 1 ? Number(mockForm) : null;
  const mockNum = formId || mockFormFromTopic(topic, lvl);
  if (mockNum && isSessionTopic(topic, lvl)) {
    return prefix ? `${prefix} · Mock ${mockNum}` : `Mock ${mockNum}`;
  }
  const base = topicLabel(topic, lvl) || 'Session';
  if (prefix && isSessionTopic(topic, lvl)) return `${prefix} · ${base}`;
  return base;
}

export function topicSlugFromName(firestoreName) {
  const match = TOPICS.find((t) => t.firestore === firestoreName);
  return match ? match.slug : null;
}

/**
 * True for a timed mock sitting: a named form (`session-1`..`session-COUNT` for `level`) or a
 * legacy hub slug / empty topic. Topic drills and out-of-range `session-N` are false (CFA-63).
 */
export function isSessionTopic(topic, level = 'l1') {
  return isSessionSitting(topic, level);
}
